import React, { useState, useLayoutEffect, useRef } from "react";
import { Helmet } from '@dr.pogodin/react-helmet';
import { gsap } from "gsap";


import { addresses } from "../data/locations";

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(addresses[0]);
  const heroRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".story-hero h1, .story-hero p", {
        opacity: 0,
        y: 50,
        duration: 1,
        stagger: 0.2,
      });
    }, heroRef);
    return () => ctx.revert();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const scriptURL =
      "https://script.google.com/macros/s/AKfycbxTj7Qj7gsqZbgsbrKeqFQdiQ5mBQZud5g1ZNLQNB_RNXguhWBW2T8lc9ZIeBTIQBsd/exec";
    const form = e.target;

    fetch(scriptURL, { method: "POST", body: new FormData(form) })
      .then((response) => {
        console.log("Success!", response);
        setIsSubmitted(true);
      })
      .catch((error) => console.error("Error!", error.message))
      .finally(() => setIsSubmitting(false));
  };

  if (isSubmitted) {
    return (
      <div className="pt-24 pb-16 px-4 min-h-[calc(100vh-300px)] flex flex-col justify-center items-center text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-serif text-4xl mb-4">Thank You</h2>
          <p className="text-base">Your message has been received. We will be in touch shortly.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Contact Us | Visit Our Stores | Mackintosh's Kilts</title>
        <meta name="description" content="Contact Mackintosh's Kilts. Visit our stores in Glasgow West End, Glasgow City, or St Andrews. Book a consultation for your bespoke kilt commission." />
        <meta name="keywords" content="contact kiltmaker, Glasgow kilt shop, St Andrews kilts, kilt consultation, visit kiltmaker, bespoke kilt appointment, Highland dress store" />
        <meta property="og:title" content="Contact Us | Visit Our Stores" />
        <meta property="og:description" content="Visit Mackintosh's Kilts in Glasgow or St Andrews. Book your consultation for a bespoke kilt commission." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.jamesmackintosh.co.uk/contact" />
        <link rel="canonical" href="https://www.jamesmackintosh.co.uk/contact" />
      </Helmet>
      <section className="pt-32 pb-8 md:pt-24 md:pb-20 px-4 md:px-8 bg-hero-bg text-center mb-6 md:mb-16" ref={heroRef}>
        <h1 className="font-serif text-3xl md:text-7xl leading-tight mb-4">Contact Us</h1>
        <p className="text-base md:text-xl text-text opacity-80 max-w-3xl mx-auto">
          Whether you have a question about our craft, a query about a
          commission, or simply wish to share your story, we would be delighted
          to hear from you.
        </p>
      </section>
      <div className="pt-6 pb-6 md:pt-16 md:pb-16 px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 max-w-6xl mx-auto px-0 md:px-8">
          <div className="bg-white p-6 md:p-12 rounded-lg shadow-[0_10px_30px_rgba(0,0,0,0.1)]">
            <form
              name="submit-to-google-sheet"
              className="flex flex-col gap-6"
              onSubmit={handleSubmit}
            >
              <input type="hidden" name="formType" value="contact" />
              <div className="flex flex-col gap-2">
                <label htmlFor="name" className="block font-semibold text-text text-sm">Full Name</label>
                <input type="text" id="name" name="name" required className="w-full p-3.5 font-sans text-base border border-border bg-[#f9f9f9] rounded transition-all duration-300 focus:outline-none focus:border-primary focus:bg-white focus:shadow-[0_0_0_3px_rgba(185,151,110,0.2)]" />
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="email" className="block font-semibold text-text text-sm">Email Address</label>
                <input type="email" id="email" name="email" required className="w-full p-3.5 font-sans text-base border border-border bg-[#f9f9f9] rounded transition-all duration-300 focus:outline-none focus:border-primary focus:bg-white focus:shadow-[0_0_0_3px_rgba(185,151,110,0.2)]" />
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="phone" className="block font-semibold text-text text-sm">Phone Number (Optional)</label>
                <input type="tel" id="phone" name="phone" className="w-full p-3.5 font-sans text-base border border-border bg-[#f9f9f9] rounded transition-all duration-300 focus:outline-none focus:border-primary focus:bg-white focus:shadow-[0_0_0_3px_rgba(185,151,110,0.2)]" />
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="message" className="block font-semibold text-text text-sm">Message</label>
                <textarea id="message" name="message" required className="w-full p-3.5 font-sans text-base border border-border bg-[#f9f9f9] rounded transition-all duration-300 focus:outline-none focus:border-primary focus:bg-white focus:shadow-[0_0_0_3px_rgba(185,151,110,0.2)] min-h-[120px] resize-y"></textarea>
              </div>
              <button
                type="submit"
                className="w-full p-4 text-lg bg-primary text-white font-medium uppercase tracking-wider hover:bg-primary-dark transition-colors duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
          <div className="p-8 bg-hero-bg rounded-lg h-fit">
            <h3 className="font-serif text-3xl mb-6 text-primary-dark">Our Locations</h3>
            <div className="flex mb-6 rounded-lg overflow-hidden border border-border flex-col sm:flex-row">
              {addresses.map((addr) => (
                <button
                  key={addr.name}
                  className={`flex-1 p-3 sm:p-4 text-sm font-medium border-none cursor-pointer transition-colors duration-300 text-center sm:border-r sm:border-border sm:last:border-r-0 border-b sm:border-b-0 last:border-b-0 ${
                    selectedAddress.name === addr.name
                      ? "bg-primary text-white"
                      : "bg-white text-primary-dark hover:bg-primary hover:text-white"
                  }`}
                  onClick={() => setSelectedAddress(addr)}
                >
                  {addr.name}
                </button>
              ))}
            </div>
            <div className="mb-6">
              <h4 className="font-serif text-2xl mb-2">{selectedAddress.name}</h4>
              <p className="text-base leading-relaxed mb-2">{selectedAddress.address}</p>
              <p className="text-base leading-relaxed mb-2">
                Phone: <a href={`tel:${selectedAddress.phone.replace(/\s+/g, '')}`} className="text-primary no-underline transition-colors duration-300 hover:text-primary-dark">{selectedAddress.phone}</a>
              </p>
            </div>
            <div className="rounded-lg overflow-hidden shadow-[0_5px_15px_rgba(0,0,0,0.1)]">
              <iframe
                src={selectedAddress.mapUrl}
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={selectedAddress.name}
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;
